import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { useI18n } from '../../context/useI18n';
import { Star, ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';

function StarRating({ value, onChange = null, disabled = false }) {
  const interactive = typeof onChange === 'function' && !disabled;
  return (
    <div className="inline-flex items-center gap-0.5" role="group" aria-label="Calificación">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={!interactive}
          className={`p-0.5 ${interactive ? 'hover:scale-110 transition' : 'cursor-default opacity-100'}`}
          onClick={() => interactive && onChange?.(n)}
          aria-label={`${n} estrellas`}
        >
          <Star
            size={18}
            className={n <= (value || 0) ? 'fill-amber-400 text-amber-400' : 'text-stone-300'}
          />
        </button>
      ))}
    </div>
  );
}

function formatClassWhen(iso) {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('es-CO', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function newCriteriaId() {
  return `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

export default function D28dCycleTrainerReportTab({ cycleId, scopeGymId = null }) {
  const { t } = useI18n();
  const [report, setReport] = useState(null);
  const [programId, setProgramId] = useState('');
  const [planName, setPlanName] = useState('');
  const [expanded, setExpanded] = useState({});
  const [msg, setMsg] = useState('');
  const [criteriaTitle, setCriteriaTitle] = useState('');
  const [criteriaList, setCriteriaList] = useState([]);
  const [criteriaBusy, setCriteriaBusy] = useState(false);
  const [showCriteria, setShowCriteria] = useState(false);

  const loadCriteria = useCallback(async () => {
    if (scopeGymId) return;
    try {
      const res = await api.get('/d28d/cycle/admin/trainer-eval/criteria');
      const data = res.data?.data;
      setCriteriaTitle(data?.title || 'Encuesta de satisfacción D28D');
      setCriteriaList(Array.isArray(data?.criteria) ? data.criteria : []);
    } catch {
      /* admin only */
    }
  }, [scopeGymId]);

  const load = useCallback(async (signal) => {
    if (!cycleId) {
      setReport(null);
      return;
    }
    const params = { cycle_id: cycleId };
    if (scopeGymId) params.gym_id = scopeGymId;
    if (programId) params.program_id = programId;
    if (planName) params.plan = planName;
    try {
      const res = await api.get('/d28d/cycle/admin/trainer-report', { params });
      if (signal?.aborted) return;
      setReport(res.data?.data || null);
    } catch {
      if (signal?.aborted) return;
      setReport(null);
    }
  }, [cycleId, scopeGymId, programId, planName]);

  useEffect(() => {
    const ac = { aborted: false };
    load(ac);
    return () => {
      ac.aborted = true;
    };
  }, [load]);
  useEffect(() => { loadCriteria(); }, [loadCriteria]);

  const programs = report?.filters?.programs || [];
  const plans = report?.filters?.plans || [];
  const trainers = report?.trainers || [];
  const summary = report?.summary;

  const toggleExpand = (hostKey) => {
    setExpanded((prev) => ({ ...prev, [hostKey]: !prev[hostKey] }));
  };

  const saveCriteria = async () => {
    setCriteriaBusy(true);
    setMsg('');
    try {
      await api.put('/d28d/cycle/admin/trainer-eval/criteria', {
        title: criteriaTitle,
        criteria: criteriaList,
        active: true,
      });
      setMsg(t('d28d.reports.criteria_saved', 'Criterios de evaluación guardados'));
      await loadCriteria();
    } catch (err) {
      setMsg(err.response?.data?.error || 'Error guardando criterios');
    } finally {
      setCriteriaBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {!scopeGymId ? (
        <div className="card p-4 space-y-3">
          <div className="flex flex-wrap justify-between gap-2 items-center">
            <div>
              <h2 className="font-semibold text-stone-900">
                {t('d28d.reports.eval_criteria_title', 'Criterios de evaluación (usuarios D28D)')}
              </h2>
              <p className="text-xs text-stone-500 mt-1">
                {t('d28d.reports.eval_criteria_hint', 'Se muestran el día 25 del ciclo a usuarios D28D plataforma (no sedes). Califican con estrellas a cada entrenador activo.')}
              </p>
            </div>
            <button
              type="button"
              className="btn btn-secondary text-xs"
              onClick={() => setShowCriteria((v) => !v)}
            >
              {showCriteria ? 'Ocultar' : 'Editar criterios'}
            </button>
          </div>

          {showCriteria ? (
            <div className="space-y-3 border-t border-stone-100 pt-3">
              <label className="block">
                <span className="ui-field__label">Título de la encuesta</span>
                <input
                  className="ui-input mt-1 w-full"
                  value={criteriaTitle}
                  onChange={(e) => setCriteriaTitle(e.target.value)}
                />
              </label>
              {criteriaList.map((c, idx) => (
                <div key={c.id || idx} className="flex gap-2 items-start">
                  <textarea
                    className="ui-input flex-1 text-sm min-h-[3.5rem]"
                    value={c.label}
                    onChange={(e) => {
                      const next = [...criteriaList];
                      next[idx] = { ...c, label: e.target.value };
                      setCriteriaList(next);
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary p-2 shrink-0"
                    title="Eliminar"
                    onClick={() => setCriteriaList(criteriaList.filter((_, i) => i !== idx))}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-secondary text-xs inline-flex items-center gap-1"
                  onClick={() => setCriteriaList([...criteriaList, { id: newCriteriaId(), label: '', required: true }])}
                >
                  <Plus className="w-3.5 h-3.5" /> Añadir criterio
                </button>
                <button
                  type="button"
                  className="btn btn-primary text-xs"
                  disabled={criteriaBusy}
                  onClick={saveCriteria}
                >
                  {criteriaBusy ? 'Guardando…' : 'Guardar criterios'}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-3 items-end">
        <label className="block">
          <span className="ui-field__label">{t('d28d.reports.program', 'Programa')}</span>
          <select className="ui-input mt-1 min-w-[10rem]" value={programId} onChange={(e) => setProgramId(e.target.value)}>
            <option value="">{t('d28d.reports.all_programs', 'Todos los programas')}</option>
            {programs.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="ui-field__label">{t('d28d.reports.plan', 'Plan')}</span>
          <select className="ui-input mt-1 min-w-[10rem]" value={planName} onChange={(e) => setPlanName(e.target.value)}>
            <option value="">{t('d28d.reports.all_plans', 'Todos los planes')}</option>
            {plans.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </label>
      </div>

      {planName ? (
        <p className="text-xs text-stone-600 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2">
          {t('d28d.reports.plan_filter_hint', 'La asistencia mostrada cuenta solo usuarios del plan «{plan}».', { plan: planName })}
        </p>
      ) : null}

      {msg ? <p className="text-sm text-stone-700">{msg}</p> : null}

      {summary ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="card p-4">
            <p className="text-xs text-stone-500">{t('d28d.reports.trainers', 'Entrenadores')}</p>
            <p className="text-2xl font-bold">{summary.trainers_count}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs text-stone-500">{t('d28d.reports.classes_in_cycle', 'Clases en ciclo')}</p>
            <p className="text-2xl font-bold">{summary.classes_count}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs text-stone-500">{t('d28d.reports.total_attendance', 'Asistencias totales')}</p>
            <p className="text-2xl font-bold">{summary.total_attendance}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs text-stone-500">{t('d28d.reports.avg_attendance', 'Promedio por clase')}</p>
            <p className="text-2xl font-bold">{summary.avg_attendance_per_class}</p>
          </div>
        </div>
      ) : null}

      <div className="card overflow-hidden">
        <div className="p-4 border-b border-stone-200">
          <h2 className="font-semibold text-stone-900">{t('d28d.reports.trainer_table_title', 'Desempeño por entrenador')}</h2>
          <p className="text-xs text-stone-500 mt-1">
            {t('d28d.reports.trainer_table_hint_users', 'Clases impartidas, asistencia y calificación promedio de usuarios (estrellas).')}
          </p>
        </div>

        {trainers.length === 0 ? (
          <p className="p-4 text-sm text-stone-600">
            {t('d28d.reports.no_trainers', 'No hay clases con entrenador en este ciclo para los filtros seleccionados.')}
          </p>
        ) : (
          <div className="divide-y divide-stone-100">
            {trainers.map((tr) => {
              const open = expanded[tr.host_key];
              const stars = Number(tr.user_rating_avg ?? tr.rating) || 0;
              return (
                <div key={tr.host_key} className="p-4 space-y-3">
                  <div className="flex flex-wrap justify-between gap-3">
                    <div>
                      <p className="font-semibold text-stone-900">{tr.host_name}</p>
                      {tr.host_email ? <p className="text-xs text-stone-500">{tr.host_email}</p> : null}
                      <p className="text-xs text-stone-500 mt-1">
                        {tr.classes_count} {t('d28d.reports.classes_given', 'clases')}
                        {' · '}
                        {tr.total_attendance} {t('d28d.reports.attendees', 'asistentes')}
                        {' · '}
                        {t('d28d.reports.avg_short', 'prom.')} {tr.avg_attendance}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1 min-w-[11rem]">
                      <StarRating value={Math.round(stars)} disabled />
                      <p className="text-xs text-stone-600">
                        {tr.user_rating_count
                          ? `${stars}/5 · ${tr.user_rating_count} evaluación(es)`
                          : t('d28d.reports.no_user_ratings', 'Sin evaluaciones de usuarios')}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="text-xs text-stone-600 inline-flex items-center gap-1 hover:text-stone-900"
                    onClick={() => toggleExpand(tr.host_key)}
                  >
                    {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    {open
                      ? t('d28d.reports.hide_classes', 'Ocultar clases')
                      : t('d28d.reports.show_classes', 'Ver clases ({count})', { count: tr.classes.length })}
                  </button>

                  {open ? (
                    <div className="overflow-x-auto rounded-lg border border-stone-200">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-stone-500 bg-stone-50">
                            <th className="p-2">{t('d28d.reports.class', 'Clase')}</th>
                            <th className="p-2">{t('d28d.reports.when', 'Fecha')}</th>
                            <th className="p-2">{t('d28d.reports.program', 'Programa')}</th>
                            <th className="p-2">{t('d28d.reports.attendance', 'Asistencia')}</th>
                            <th className="p-2">{t('d28d.reports.enrolled', 'Inscritos')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tr.classes.map((cls) => (
                            <tr key={cls.class_id} className="border-t border-stone-100">
                              <td className="p-2 font-medium text-stone-800">{cls.title}</td>
                              <td className="p-2 whitespace-nowrap">{formatClassWhen(cls.start_time)}</td>
                              <td className="p-2">{cls.program_name}</td>
                              <td className="p-2 font-semibold">{cls.attendance}</td>
                              <td className="p-2">{cls.enrolled}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
